import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { resolveUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import ShareModal from "@/components/ShareModal";
import { detectVideoSource } from "@/lib/videoSource";
import { Play, Pause, ChevronLeft, ChevronRight, CheckCircle2, Award, FileText, Sparkles, Share2 } from "lucide-react";

const CINEMA_BG = "https://static.prod-images.emergentagent.com/jobs/e31510f3-0985-434c-b5ba-05b91fac6a7c/images/97edf4a0162827fe7b25bc8d7ff0c0f636e009e8a3882214a7baaf425a3f8c6c.png";

export default function Watch() {
    const { slug, moduleId } = useParams();
    const navigate = useNavigate();
    const [journey, setJourney] = useState(null);
    const [progress, setProgress] = useState(null);
    const [speed, setSpeed] = useState(1);
    const [isPlaying, setIsPlaying] = useState(false);
    const [certificate, setCertificate] = useState(null);
    const [shareOpen, setShareOpen] = useState(false);
    const [shareVariant, setShareVariant] = useState("completion");
    const { user } = useAuth();
    const videoRef = useRef(null);
    const saveTimer = useRef(null);

    useEffect(() => {
        document.body.classList.add("cinema-mode");
        return () => document.body.classList.remove("cinema-mode");
    }, []);

    useEffect(() => {
        api.get(`/journeys/${slug}`).then(r => setJourney(r.data));
    }, [slug]);

    useEffect(() => {
        if (journey) api.get(`/progress/${journey.id}`).then(r => setProgress(r.data)).catch(()=>{});
    }, [journey, moduleId]);

    const currentModule = journey?.modules.find(m => (m.lessons || []).some(l => l.id === moduleId));
    const currentLesson = currentModule?.lessons?.find(l => l.id === moduleId);
    // Build a flat ordered list of all lessons across modules for prev/next navigation
    const flatLessons = journey ? journey.modules.flatMap(m => (m.lessons || []).map(l => ({ ...l, _module: m }))) : [];
    const flatIdx = flatLessons.findIndex(l => l.id === moduleId);
    const prev = flatIdx > 0 ? flatLessons[flatIdx - 1] : null;
    const next = flatIdx >= 0 && flatIdx < flatLessons.length - 1 ? flatLessons[flatIdx + 1] : null;

    // Detect video source type (direct file vs YouTube/Vimeo iframe)
    const videoSource = useMemo(
        () => detectVideoSource(currentLesson?.video_url || ""),
        [currentLesson?.video_url]
    );

    const completedSet = new Set((progress?.items || []).filter(i => i.completed).map(i => i.module_id));

    const saveProgress = useCallback(async (completed = false) => {
        if (!journey || !currentLesson) return;
        try {
            const { data } = await api.post("/progress", {
                journey_id: journey.id,
                module_id: currentLesson.id,
                position_seconds: videoRef.current?.currentTime || 0,
                completed,
            });
            if (data.certificate) setCertificate(data.certificate);
            // re-fetch progress
            api.get(`/progress/${journey.id}`).then(r => setProgress(r.data)).catch(()=>{});
        } catch { /* ignore */ }
    }, [journey, currentLesson]);

    // Save every 10s (only when playing a direct file — iframes don't expose currentTime)
    useEffect(() => {
        saveTimer.current && clearInterval(saveTimer.current);
        if (videoSource.kind === "file") {
            saveTimer.current = setInterval(() => { if (isPlaying) saveProgress(false); }, 10000);
        }
        return () => clearInterval(saveTimer.current);
    }, [isPlaying, saveProgress, videoSource.kind]);

    const onEnded = async () => {
        await saveProgress(true);
        if (next) navigate(`/assistir/${slug}/${next.id}`);
    };

    const togglePlay = () => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) videoRef.current.play(); else videoRef.current.pause();
    };

    const changeSpeed = (s) => {
        setSpeed(s);
        if (videoRef.current) videoRef.current.playbackRate = s;
    };

    if (!journey || !currentLesson) return (
        <div className="min-h-screen bg-[#0A0A0A] text-white"><Header cinema /><div className="px-10 py-20">Carregando...</div></div>
    );

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-white relative" style={{ backgroundImage: `url(${CINEMA_BG})`, backgroundSize: "cover", backgroundAttachment: "fixed" }}>
            <div className="absolute inset-0 bg-[#0A0A0A]/85" />
            <div className="relative">
                <Header cinema />

                <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-8 grid lg:grid-cols-[1fr_360px] gap-8">
                    {/* Player */}
                    <div>
                        <div className="rounded-2xl overflow-hidden bg-black border border-white/10 shadow-2xl">
                            {videoSource.kind === "file" ? (
                                <video
                                    ref={videoRef}
                                    data-testid="cinema-video"
                                    key={currentLesson.id}
                                    src={resolveUrl(videoSource.embedUrl)}
                                    controls
                                    autoPlay
                                    onPlay={() => setIsPlaying(true)}
                                    onPause={() => setIsPlaying(false)}
                                    onEnded={onEnded}
                                    className="w-full aspect-video bg-black"
                                />
                            ) : (
                                <iframe
                                    data-testid="cinema-iframe"
                                    key={currentLesson.id}
                                    src={videoSource.embedUrl}
                                    title={currentLesson.title}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    className="w-full aspect-video bg-black border-0"
                                />
                            )}
                        </div>

                        {/* Controls bar (extra) */}
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            {videoSource.kind === "file" ? (
                                <>
                                    <button data-testid="player-toggle" onClick={togglePlay} className="btn-ghost !bg-white/10 !text-white !border-white/10">
                                        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                        {isPlaying ? "Pausar" : "Reproduzir"}
                                    </button>
                                    <div className="flex items-center gap-1 bg-white/5 rounded-full p-1 border border-white/10">
                                        {[0.75, 1, 1.25, 1.5, 2].map(s => (
                                            <button key={s} onClick={() => changeSpeed(s)}
                                                    data-testid={`speed-${s}`}
                                                    className={`text-xs font-display px-3 py-1.5 rounded-full transition ${speed === s ? "bg-white text-black" : "text-white/70 hover:text-white"}`}>
                                                {s}x
                                            </button>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="text-xs font-body text-white/50 px-2">
                                    Reprodução via {videoSource.kind === "youtube" ? "YouTube" : "Vimeo"} — controles, velocidade e fullscreen dentro do player.
                                </div>
                            )}
                            <button data-testid="mark-complete" onClick={() => saveProgress(true)} className="btn-ghost !bg-[#D4AF37]/20 !text-[#F3E5AB] !border-[#D4AF37]/40 ml-auto">
                                <CheckCircle2 className="w-4 h-4" /> Marcar como concluído
                            </button>
                            <button
                                data-testid="share-module"
                                onClick={() => { setShareVariant("completion"); setShareOpen(true); }}
                                className="btn-ghost !bg-white/10 !text-white !border-white/15"
                            >
                                <Share2 className="w-4 h-4" /> Compartilhar
                            </button>
                        </div>

                        {/* Module + Lesson info */}
                        <div className="mt-8">
                            <div className="overline text-white/60">Módulo {currentModule.order} · {currentModule.title} · {journey.title}</div>
                            <h1 className="font-display text-3xl md:text-4xl mt-2 tracking-tight">{currentLesson.title}</h1>
                            <p className="font-body text-white/70 mt-4 max-w-3xl">{currentLesson.description}</p>
                        </div>

                        {/* Resources */}
                        {currentLesson.resources?.length > 0 && (
                            <div className="mt-10">
                                <div className="overline text-white/60 mb-3">Materiais complementares</div>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {currentLesson.resources.map((r, i) => (
                                        <a key={i} href={resolveUrl(r.url)} target="_blank" rel="noreferrer"
                                           data-testid={`resource-${i}`}
                                           className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#D4AF37]/40 transition">
                                            {r.type === "pdf" ? <FileText className="w-5 h-5 text-[#F3E5AB]" /> : <Sparkles className="w-5 h-5 text-[#00D4D4]" />}
                                            <div>
                                                <div className="font-display text-sm">{r.title}</div>
                                                <div className="font-body text-xs text-white/50 uppercase tracking-wider">{r.type}</div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Prev/Next */}
                        <div className="flex justify-between mt-10">
                            <button disabled={!prev} onClick={() => prev && navigate(`/assistir/${slug}/${prev.id}`)}
                                    data-testid="prev-module" className={`btn-ghost !bg-white/10 !text-white !border-white/10 ${!prev && "opacity-30 pointer-events-none"}`}>
                                <ChevronLeft className="w-4 h-4" /> Aula anterior
                            </button>
                            <button disabled={!next} onClick={() => next && navigate(`/assistir/${slug}/${next.id}`)}
                                    data-testid="next-module" className={`btn-ghost !bg-white/10 !text-white !border-white/10 ${!next && "opacity-30 pointer-events-none"}`}>
                                Próxima aula <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>

                        {certificate && (
                            <div data-testid="certificate-toast" className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#00D4D4]/10 border border-[#D4AF37]/40">
                                <div className="flex flex-wrap items-center gap-3">
                                    <Award className="w-6 h-6 text-[#F3E5AB]" />
                                    <div>
                                        <div className="font-display text-lg">Você concluiu esta jornada!</div>
                                        <div className="font-body text-sm text-white/70">Seu certificado foi emitido.</div>
                                    </div>
                                    <div className="ml-auto flex gap-2">
                                        <button
                                            data-testid="share-journey-completion"
                                            onClick={() => { setShareVariant("certificate"); setShareOpen(true); }}
                                            className="btn-ghost !bg-white/10 !text-white !border-white/15"
                                        >
                                            <Share2 className="w-4 h-4" /> Compartilhar
                                        </button>
                                        <Link to={`/certificado/${certificate.id}`} className="btn-holo">Ver certificado</Link>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar — modules with lessons inside */}
                    <aside className="rounded-2xl bg-[#111]/70 border border-white/10 p-4 lg:sticky lg:top-[88px] self-start max-h-[calc(100vh-110px)] overflow-y-auto no-scrollbar">
                        <div className="overline text-white/60 px-3 pt-2">Jornada</div>
                        <div className="font-display text-lg px-3 mt-1 mb-4 line-clamp-2">{journey.title}</div>
                        <div className="space-y-5">
                            {journey.modules.map((m) => {
                                const lessons = m.lessons || [];
                                const doneCount = lessons.filter(l => completedSet.has(l.id)).length;
                                return (
                                    <div key={m.id} data-testid={`sidebar-module-${m.order}`}>
                                        <div className="px-3 pb-2 flex items-center justify-between">
                                            <div className="min-w-0">
                                                <div className="overline text-white/40">Módulo {String(m.order).padStart(2, "0")}</div>
                                                <div className="font-display text-sm text-white/90 mt-0.5 line-clamp-2">{m.title}</div>
                                            </div>
                                            <div className="font-body text-[10px] text-white/40 ml-2 shrink-0">{doneCount}/{lessons.length}</div>
                                        </div>
                                        <ol className="space-y-1">
                                            {lessons.map((l) => {
                                                const isActive = l.id === currentLesson.id;
                                                const done = completedSet.has(l.id);
                                                return (
                                                    <li key={l.id}>
                                                        <button
                                                            data-testid={`sidebar-lesson-${m.order}-${l.order}`}
                                                            onClick={() => navigate(`/assistir/${slug}/${l.id}`)}
                                                            className={`cinema-list-item ${isActive ? "active" : ""} text-left w-full flex items-start gap-3 p-3 rounded-lg`}
                                                        >
                                                            <div className={`font-display text-xs mt-0.5 ${isActive ? "text-[#F3E5AB]" : "text-white/40"}`}>
                                                                {m.order}.{l.order}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className={`font-display text-sm leading-snug line-clamp-2 ${isActive ? "text-white" : "text-white/80"}`}>
                                                                    {l.title}
                                                                </div>
                                                                <div className="font-body text-xs text-white/40 mt-1">{l.duration_minutes} min</div>
                                                            </div>
                                                            {done && <CheckCircle2 className="w-4 h-4 text-[#00D4D4] flex-shrink-0" />}
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ol>
                                    </div>
                                );
                            })}
                        </div>
                    </aside>
                </div>
            </div>
            <ShareModal
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                variant={shareVariant}
                userName={user?.name || "Aluno IMQ"}
                journeyTitle={journey.title}
                journeyCover={journey.cover}
                hours={Math.round((journey.total_minutes || 0) / 60 * 10) / 10}
                moduleTitle={currentLesson.title}
            />
        </div>
    );
}
